import React, { useCallback, useEffect, useState } from 'react';
import '../publishing-dialog.css';
import VersionsIcon from '../../../assets/versions.svg';
import Button from '../../../lib/button/Button';
import PublishingSequenceContent from './PublishingSequenceContent';
import Tooltip from '../../../lib/tooltip/Tooltip';
import { EditorService } from '../../../services/editor';
import DialogLoader from '../../DialogLoader';
import { getCurrentUserOrLogoutRedirect } from '../../../auth';
import {
	InfrastructureResourceType,
	IUser,
	PublicationStatus,
} from '@elyx-code/definitions';
import { PublicationRun } from '../../../services/editor/publication/publication';
import dayjs from 'dayjs';
import UserChip from '../../user/UserChip';
import { popupNotification } from '../../../popup-notification';
import axios from 'axios';
import { envConfig } from '../../../config';

export interface IPublishingSequenceDialogProps {
	onClose: (event: any) => void;
	contentHeight?: boolean;
	project: EditorService;
}

const PublishingSequenceDialog: React.FC<IPublishingSequenceDialogProps> = ({
	onClose,
	project,
}) => {
	const [user, setUser] = useState<IUser | null>(null);
	const [
		isPublicationCompletedSuccessfully,
		setIsPublicationCompletedSuccessfully,
	] = useState<boolean>(
		project.publication?.finalStatus === PublicationStatus.Success,
	);
	const [activePub, setActivePub] = useState(project.publication);

	const onPublicationCompleted = useCallback(() => {
		setIsPublicationCompletedSuccessfully(true);
	}, []);

	const onSpecificPublicationLoaded = useCallback((pub: PublicationRun) => {
		setActivePub(pub);
	}, []);

	const onPublicationUpdated = useCallback((pub: PublicationRun) => {
		if (activePub && pub.id === activePub.id) {
			setActivePub(pub);
		}
	}, [activePub]);

	useEffect(() => {
		project.on('project-publication-completed', onPublicationCompleted);

		project.on('specific-publication-loaded', onSpecificPublicationLoaded);

		project.on('publication-updated', onPublicationUpdated);

		return () => {
			project.off('project-publication-completed', onPublicationCompleted);
			project.off('specific-publication-loaded', onSpecificPublicationLoaded);
		project.off('publication-updated', onPublicationUpdated);

		};
	}, [onPublicationCompleted, onSpecificPublicationLoaded]);

	useEffect(() => {
		const fetchUser = async () => {
			const user = await getCurrentUserOrLogoutRedirect();
			setUser(user);
		};

		fetchUser();
	}, [project]);

	if (!project || !project.logic || !project.ready) {
		return (
			<DialogLoader
				title='Project publication'
				subtitle={
					activePub?.status === PublicationStatus.Running
						? 'Publishing your project to the cloud'
						: 'Publication sequence'
				}
				onClose={onClose}
			/>
		);
	}

	let header = 'Publishing version';

	if (activePub?.status === PublicationStatus.Success) {
		header = 'Published version';
	} else if (
		activePub?.status === PublicationStatus.Failed ||
		activePub?.finalStatus === PublicationStatus.Failed
	) {
		header = 'Publication failed';
	} else if (
		activePub?.status === PublicationStatus.Reverted ||
		activePub?.status === PublicationStatus.Unpublished
	) {
		header = 'Publication reverted';
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				gap: '4px',
				padding: '40px',
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
				<button className='dialog_back_button' onClick={onClose}>
					<i className='fa-solid fa-arrow-left' />
				</button>
				<h1 style={{ margin: 0, fontWeight: 500, fontSize: '28px' }}>
					Project publication
				</h1>
			</div>
			<a
				style={{
					display: 'flex',
					gap: '6px',
					marginLeft: '42px',
					textDecoration: 'none',
					flexShrink: 0,
				}}
				href=''
				onClick={(event) => {
					event.preventDefault();
					EditorService.navigate(
						`${project?.editorBasePath}/publication/history`,
					);
				}}
			>
				<img src={VersionsIcon} alt='Versions' />
				<p style={{ margin: 0, color: 'rgba(0,0,0,0.7)' }}>Publication history</p>
			</a>

			<div style={{ flex: 1, margin: '0 42px', marginTop: '30px', minHeight: 0, overflowY: 'auto' }}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						minHeight: '80%',
					}}
				>
					<h3
						style={{
							display: 'flex',
							gap: '6px',
							fontWeight: 'normal',
							margin: 0,
						}}
					>
						<span>{header}</span>
						<span style={{ fontWeight: 'bold' }}>
							{project.publication?.projectPublishedSemanticVersion}
						</span>
					</h3>
					{activePub?.status === PublicationStatus.Running ? (
						<div
							style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
						>
							<p
								style={{
									margin: 0,
									fontSize: '14px',
									marginTop: '6px',
									color: 'rgba(0,0,0,0.54)',
								}}
							>
								This is where the magic happens.
							</p>
							<p
								style={{
									margin: 0,
									fontSize: '14px',
									marginTop: '6px',
									color: 'rgba(0,0,0,0.54)',
								}}
							>
								Work equivalent to 10 full-time software engineers over a few
								months is being generated.
								<br />
								The first time a project is published it might take a few
								minutes.
							</p>
							<p
								style={{
									margin: 0,
									fontSize: '14px',
									marginTop: '6px',
									color: 'rgba(0,0,0,0.54)',
								}}
							>
								{`You will receive an email at `}
								<b>{user?.email}</b>
								{` once the new version is published.`}
							</p>
						</div>
					) : (
						<div
							style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
						>
							<p
								style={{
									margin: 0,
									fontSize: '14px',
									marginTop: '6px',
									color: 'rgba(0,0,0,0.54)',
								}}
							>
								Summary of publication from{' '}
								{dayjs(activePub?.createdAt).format('MMM D, YYYY h:mm A')}
							</p>
							<p
								style={{
									margin: 0,
									fontSize: '14px',
									marginTop: '6px',
									color: 'rgba(0,0,0,0.54)',
								}}
							>
								Published by
							</p>
							<UserChip user={{ id: activePub.triggeredBy }} />
						</div>
					)}
					<PublishingSequenceContent
						project={project}
						onClose={onClose}
					/>
				</div>
			</div>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					width: '100%',
					flexShrink: 0,
				}}
			>
				<div>
					{!!activePub && (
						activePub.status === PublicationStatus.Failed || activePub.finalStatus === PublicationStatus.Failed ? (
							<>
								<Button
									id='retry-publication-button'
									onClick={async () => {
										try {
											await project.publish();
										} catch (error) {
											popupNotification({
												text: 'Failed to retry publication',
												color: 'error',
											});
										}
									}}
									color='primary'
								>
									Retry
								</Button>
								<Tooltip
									anchorQuerySelector='#retry-publication-button'
									placement='bottom'
								>
									Retry publishing the project.
								</Tooltip>
							</>
						) : (
							<>
								<Button
									id='abort-publication-button'
									disabled={
										!activePub || PublicationStatus.Running !== activePub.status
									}
									onClick={async () => {
										try {
											const accessToken = await project.getAccessToken();

											await axios.post(
												`${envConfig.API_BASE_URL}/publishing-service/v1/${project.projectId}/publications/${activePub?.id}/abort`,
												{},
												{
													headers: {
														Authorization: 'Bearer ' + accessToken,
													},
												},
											);

											const updatedPu = await project.loadGivenPublication(activePub.id);
											setActivePub(updatedPu);
											// Refresh the page
											// window.location.reload();
										} catch (error) {
											popupNotification({
												text: 'Failed to abort publication',
												color: 'error',
											});
										}
									}}
									color='error'
								>
									Abort
								</Button>
								<Tooltip
									anchorQuerySelector='#abort-publication-button'
									placement='bottom'
								>
									{!activePub || PublicationStatus.Running !== activePub.status
										? 'The publication is not currently running'
										: 'Abort the publication process. This will stop all ongoing steps of the publication.'}
								</Tooltip>
							</>
						)
					)}
				</div>
				<div
					style={{
						alignSelf: 'flex-start',
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'flex-end',
						width: '100%',
						gap: '16px',
					}}
				>
					<Button
						onClick={() => {
							EditorService.navigate(`${project?.editorBasePath}`);
						}}
						color='primary'
						variant='text'
					>
						Back to canvas
					</Button>
					<Button
						onClick={() => {
							EditorService.navigate(
								`${project?.editorBasePath}/resources`,
							);
						}}
						disabled={!isPublicationCompletedSuccessfully}
						id='see-live-resources-publication-button'
						color='primary'
					>
						See live assets
					</Button>
					<Tooltip
						anchorQuerySelector='#see-live-resources-publication-button'
						placement='bottom'
					>
						{isPublicationCompletedSuccessfully
							? 'See the cloud resources employed by this project after its publication'
							: 'The resources are being provisioned. This might take a few minutes.'}
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default PublishingSequenceDialog;
